import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import Game from './Game'; // For the game page
import ExperimentalProjects from './ExperimentalProjects'; // For experimental projects
import WorkflowBuilder from './WorkflowBuilder'; // For workflow builder
import './App.css';

// This is the main component for your portfolio page
const Portfolio = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Your project data
  const projects = [
    {
      title: 'HTML5 Interactive Game',
      description: 'A creative web-based game built with HTML5 and JavaScript, featuring interactive elements and engaging gameplay.',
      tech: ['HTML5', 'JavaScript', 'CSS3', 'Canvas API'],
      image: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NTI0MzEyNzZ8MA&ixlib=rb-4.1.0&q=85',
      link: '/game',
      type: 'Game'
    },
    {
      title: 'Data Visualization Art',
      description: 'Interactive data art pieces created with p5.js, transforming complex datasets into beautiful visual narratives.',
      tech: ['p5.js', 'Python', 'Data Processing', 'Creative Coding'],
      image: 'https://images.unsplash.com/photo-1642192196483-7a8920483769?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxnZW5lcmF0aXZlJTIwYXJ0fGVufDB8fHx8MTc1MjQ5NzcxM3ww&ixlib=rb-4.1.0&q=85',
      type: 'Art'
    },
    {
      title: 'Heritage Data Analysis',
      description: 'Python-based analysis of historical data, uncovering patterns and insights from heritage information systems.',
      tech: ['Python', 'Pandas', 'Data Analysis', 'Visualization'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NTI0MzEyNzZ8MA&ixlib=rb-4.1.0&q=85',
      type: 'Data'
    },
    {
      title: 'Generative Art Series',
      description: 'A collection of algorithmic art pieces exploring the intersection of neuroscience concepts and visual creativity.',
      tech: ['Processing', 'p5.js', 'Algorithms', 'Creative Coding'],
      image: 'https://images.unsplash.com/photo-1642192248085-a40d80a208bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxnZW5lcmF0aXZlJTIwYXJ0fGVufDB8fHx8MTc1MjQ5NzcxM3ww&ixlib=rb-4.1.0&q=85',
      type: 'Art'
    },
    {
      title: 'Data Journalism Projects',
      description: 'Investigative pieces combining data analysis with storytelling to reveal verifiable insights and truths.',
      tech: ['Python', 'Data Analysis', 'Web Scraping', 'Journalism'],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NTI0MzEyNzZ8MA&ixlib=rb-4.1.0&q=85',
      type: 'Journalism'
    },
    {
      title: 'Interactive Experiments',
      description: 'Web-based experiments exploring human-computer interaction and creative digital experiences.',
      tech: ['JavaScript', 'HTML5', 'CSS3', 'Interactive Design'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NTI0MzEyNzZ8MA&ixlib=rb-4.1.0&q=85',
      type: 'Experiment',
      link: '/experimental'
    }
  ];

  // Your experience data
  const experience = [
      {
        year: '2024',
        title: 'Creative Developer & Heritage Professional',
        company: 'Freelance + HES',
        description: 'Continuing heritage data management work while developing creative coding projects. Contributing to Scotland\'s digital heritage through data visualization and interactive experiences.',
        skills: ['Python', 'p5.js', 'Heritage Data', 'Creative Coding']
      },
      {
        year: '2023',
        title: 'Heritage Information & Business Support Assistant',
        company: 'Historic Environment Scotland',
        description: 'Managing heritage data systems, financial transactions, and supporting Listed Buildings and Scheduled Monuments regulatory functions. Contributing to Scotland\'s Rock Art Project and Trove.scot website development.',
        skills: ['Heritage Management System', 'Fusion Procurement', 'Data Analysis', 'GIS', 'VBA & Macros']
      },
      {
        year: '2022',
        title: 'Test Engineer',
        company: 'Freelance',
        description: 'Conducted E2E testing on telecommunications networks (4G, LTE, VoLTE, 3G). Managed defect logging and quality assurance processes using HP Quality Centre.',
        skills: ['Network Testing', 'Quality Assurance', 'HP QC ALM', 'Technical Analysis']
      },
      {
        year: '2022',
        title: 'File and Data Reviewer',
        company: 'Scottish Child Abuse Inquiry',
        description: 'Conducted sensitive document review and data quality control for historical records. Maintained electronic databases ensuring security and integrity of inquiry materials.',
        skills: ['Data Review', 'Quality Control', 'Database Management', 'Research']
      },
      {
        year: '2021',
        title: 'Graduate Switch Program',
        company: 'Jolt.io',
        description: 'Completed intensive program covering Business Strategy, Product Development, Marketing, Sales, and Data Analysis. Bridged neuroscience background with business and technology skills.',
        skills: ['Business Strategy', 'Product Development', 'Data Analysis', 'Marketing']
      },
      {
        year: '2016',
        title: 'BSc Hons Neuroscience Graduate',
        company: 'University of Edinburgh',
        description: 'Graduated with Honours in Neuroscience. Awarded as Gifted International Citizen for work in international politics. Student ambassador for "Our Changing World" programme.',
        skills: ['Neuroscience', 'Research Methods', 'Scientific Writing', 'International Relations']
      }
    ];

  return (
    <div className="bg-slate-900 text-white min-h-screen">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-900/90 backdrop-blur-lg border-b border-slate-800/50' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              Fabricated.Space
            </div>
            <div className="hidden md:flex space-x-8">
              {['Home', 'About', 'Skills', 'Projects', 'Experience', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase())} className="text-slate-300 hover:text-white transition-colors duration-200 hover:scale-105">
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1607799279861-4dd421887fb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxkZXZlbG9wZXJ8ZW58MHx8fGJsdWV8MTc1MjQ5NTgzOXww&ixlib=rb-4.1.0&q=85)' }}>
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-6xl md:text-8xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Niall Brennan
            </h1>
            <h2 className="text-3xl md:text-4xl font-light mb-6 text-slate-300">
              Creative Developer
            </h2>
            <p className="text-xl md:text-2xl text-slate-400 mb-8 max-w-3xl mx-auto">
              Building innovative digital experiences through code, data, and creative experimentation. 
              <br />A playground for curiosity and meaningful projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => scrollToSection('projects')} className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white font-semibold hover:scale-105 transition-transform duration-200 shadow-lg hover:shadow-blue-500/25">
                Explore My Work
              </button>
              <button onClick={() => window.open('https://linktr.ee/fabricated.space', '_blank')} className="px-8 py-4 border-2 border-white/20 rounded-xl text-white font-semibold hover:bg-white/10 transition-colors duration-200 backdrop-blur-sm">
                Connect
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            About Me
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1494173853739-c21f58b16055?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx3b3Jrc3BhY2V8ZW58MHx8fGJsdWV8MTc1MjQ5NTg0OXww&ixlib=rb-4.1.0&q=85" alt="Creative workspace" className="rounded-2xl shadow-2xl" />
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-600/20 rounded-2xl"></div>
            </div>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50">
                <p className="text-xl text-slate-300 leading-relaxed mb-6">
                  I believe that new spaces for creation and community should be accessible to all, fostering connection in both our online and offline worlds. This site is my contribution to that idea.
                </p>
                <p className="text-lg text-slate-400 leading-relaxed mb-6">
                  As a creative developer, I work freelance on projects I enjoy and think are worthwhile. With a background in neuroscience from the University of Edinburgh, I bring a unique perspective to problem-solving and data interpretation.
                </p>
                <p className="text-lg text-slate-400 leading-relaxed">
                  My passion lies in creating meaningful digital experiences that tell stories through data, art, and interactive design. I'm particularly interested in verifiable information and data journalism that reveals truth.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <span className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm">Data Storytelling</span>
                <span className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm">Creative Coding</span>
                <span className="px-4 py-2 bg-pink-500/20 text-pink-300 rounded-full text-sm">Digital Art</span>
                <span className="px-4 py-2 bg-green-500/20 text-green-300 rounded-full text-sm">Neuroscience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-6 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxkZXZlbG9wZXJ8ZW58MHx8fGJsdWV8MTc1MjQ5NTgzOXww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            Skills & Tools
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { 
                category: 'Heritage & Data Systems', 
                skills: ['Heritage Management System', 'Fusion Procurement', 'GIS & Canmore', 'NRHE Database', 'Trove.scot'], 
                color: 'blue',
                icon: '🏛️'
              },
              { 
                category: 'Creative & Technical', 
                skills: ['Python', 'p5.js/Processing', 'VBA & Macros', 'Data Visualization', 'Creative Coding'], 
                color: 'purple',
                icon: '🎨'
              },
              { 
                category: 'Business & Analysis', 
                skills: ['Microsoft Office Suite', 'Financial Administration', 'Project Coordination', 'Quality Control', 'Technical Reporting'], 
                color: 'pink',
                icon: '📊'
              },
              { 
                category: 'Testing & Research', 
                skills: ['Network Testing', 'HP QC ALM', 'Document Review', 'Research Methods', 'Data Quality Control'], 
                color: 'green',
                icon: '🔍'
              }
            ].map((group, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-colors duration-300">
                <div className="text-4xl mb-4 text-center">{group.icon}</div>
                <h3 className={`text-xl font-semibold mb-4 text-${group.color}-400 text-center`}>
                  {group.category}
                </h3>
                <div className="space-y-3">
                  {group.skills.map((skill, skillIndex) => (
                    <div key={skillIndex} className="text-center">
                      <span className="text-slate-300 text-sm">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            Featured Projects
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group relative overflow-hidden rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
                <div className="aspect-video overflow-hidden">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white">{project.title}</h3>
                  <p className="text-slate-400 mb-4 text-sm">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, techIndex) => (
                      <span key={techIndex} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">{tech}</span>
                    ))}
                  </div>
                  {project.link && (
                    <Link to={project.link} className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors text-sm">
                      View Project →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-6 bg-slate-800/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              Professional Journey
            </h2>
            <p className="text-slate-400 mb-4">A timeline of meaningful work and continuous learning</p>
            <button onClick={() => window.open('https://linktr.ee/fabricated.space', '_blank')} className="text-slate-400 hover:text-white transition-colors duration-200 text-sm">
              💼 View Full CV & Links
            </button>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-blue-500 to-purple-600"></div>
            {experience.map((exp, index) => (
              <div key={index} className={`relative mb-12 ${index % 2 === 0 ? 'ml-auto pl-12' : 'mr-auto pr-12'} w-1/2`}>
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-colors duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold">
                      {exp.year.slice(-2)}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-semibold text-white">{exp.title}</h3>
                      <p className="text-blue-400">{exp.company}</p>
                    </div>
                  </div>
                  <p className="text-slate-400 mb-4">{exp.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {exp.skills.map((skill, skillIndex) => (
                      <span key={skillIndex} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">{skill}</span>
                    ))}
                  </div>
                </div>
                <div className={`absolute top-6 ${index % 2 === 0 ? '-left-3' : '-right-3'} w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full border-4 border-slate-900`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 relative">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1630233313373-a03df7d139c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHx3b3Jrc3BhY2V8ZW58MHx8fGJsdWV8MTc1MjQ5NTg0OXww&ixlib=rb-4.1.0&q=85)' }}></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            Let's Connect
          </h2>
          <div className="text-center max-w-4xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-12 border border-slate-700/50">
              <p className="text-2xl text-slate-300 mb-8">
                Interested in collaborating on meaningful projects? Let's explore the possibilities together.
              </p>
              <div className="space-y-6">
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl flex items-center justify-center">
                      <span className="text-white text-2xl">📧</span>
                    </div>
                    <div className="text-left">
                      <p className="text-slate-400">Email</p>
                      <a href="mailto:Niallbre9@gmail.com" className="text-xl text-white hover:text-green-300 transition-colors">
                        Niallbre9@gmail.com
                      </a>
                    </div>
                  </div>
                <div className="flex items-center justify-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
                    <span className="text-white text-2xl">💻</span>
                  </div>
                  <div className="text-left">
                    <p className="text-slate-400">GitHub</p>
                    <a href="https://github.com/Niallbre9" target="_blank" rel="noopener noreferrer" className="text-xl text-white hover:text-purple-300 transition-colors">
                      github.com/Niallbre9
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12">
                <button onClick={() => window.open('https://linktr.ee/fabricated.space', '_blank')} className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white font-semibold hover:scale-105 transition-transform duration-200 text-lg">
                  Get In Touch
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400 mb-4">
            © 2025 Niobiwan - Niall Brennan - Fabricated.Space
          </p>
          <p className="text-slate-500 text-sm">
            A personal and creative playground, built on the idea that the most innovative spaces are the ones we imagine and build ourselves.
          </p>
        </div>
      </footer>
    </div>
  );
};

// This is the main App component that handles the routing
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Portfolio />} />
          <Route path="/game" element={<Game />} />
          <Route path="/experimental" element={<ExperimentalProjects />} />
          <Route path="/experimental/workflow-builder" element={<WorkflowBuilder />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
